// t(key, params): the one way the web app turns a catalogue key into words, with the same checks as
// @waypoint/surface's t(): an unknown key, a missing parameter or a stray one is a type error, and
// at run time a missing parameter throws rather than rendering an empty slot.
import { en, type MessageKey } from "./en.js";

export type { MessageKey } from "./en.js";

type Placeholders<S extends string> = S extends `${string}{${infer P}}${infer Rest}`
  ? P | Placeholders<Rest>
  : never;

type TArgs<K extends MessageKey> = [Placeholders<(typeof en)[K]>] extends [never]
  ? []
  : [params: { readonly [P in Placeholders<(typeof en)[K]>]: string | number }];

/** A key whose message takes no parameters: the type for a label kept in a table. */
export type PlainKey = {
  [K in MessageKey]: [Placeholders<(typeof en)[K]>] extends [never] ? K : never;
}[MessageKey];

const PLACEHOLDER = /\{(\w+)\}/g;

export function t<K extends MessageKey>(key: K, ...args: TArgs<K>): string {
  const params: Readonly<Record<string, unknown>> = args[0] ?? {};
  return en[key].replace(PLACEHOLDER, (_match, name: string) => {
    const value = params[name];
    if (typeof value !== "string" && typeof value !== "number")
      throw new Error(`t("${key}"): missing parameter "${name}"`);
    return String(value);
  });
}
